import { MAIN_CONTENT_ID, MOBILE_NAV_ID } from '@/components/layouts/AppLayout/AppLayout';
import { Button } from '@/components/ui/Button/Button';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { cn } from '@/utils';
import { ChevronLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { APP_HEADER_HEIGHT_IN_PX } from '../AppHeader/AppHeader';

interface PageHeadingProps {
  children: React.ReactNode;
  withBackButton?: boolean;
  backURL?: string;
  isSubpage?: boolean;
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'div';
  className?: string;
  containerClassName?: string;
}

export const PageHeading = ({ children, isSubpage, withBackButton = isSubpage, backURL, as = 'h1', className, containerClassName }: PageHeadingProps) => {
  const { t } = useTranslation('components', { keyPrefix: 'pageHeading' });

  const isDesktop = useMediaQuery('desktop');

  const navigate = useNavigate();
  const location = useLocation();

  const Heading = as;

  const handleBack = () => {
    if (backURL) {
      void navigate(backURL, { replace: true });
    } else {
      const canGoBack = globalThis.history.length > 1;

      if (!canGoBack) {
        void navigate('/', { replace: true });
        return;
      }

      if ([`#${MOBILE_NAV_ID}`, `#${MAIN_CONTENT_ID}`].includes(location.hash)) {
        void navigate(-2);
      } else {
        void navigate(-1);
      }
    }
  };

  return (
    <div
      className={cn(
        'mb-6 flex w-full items-center gap-4',
        {
          'border-outline-neutral bg-fill-background-neutral fixed top-[var(--system-banner-height,0px)] left-0 z-50 border-b px-5 pt-4 pb-3':
            isSubpage && !isDesktop,
        },
        containerClassName,
      )}
      style={{
        height: isSubpage && !isDesktop ? `${APP_HEADER_HEIGHT_IN_PX}px` : 'initial',
      }}
    >
      {withBackButton && (
        <Button
          variant='icon'
          size='icon-l'
          onClick={handleBack}
          role='link'
          className='flex items-center justify-center px-2.5'
          aria-label={t('backButton.ariaLabel')}
        >
          <ChevronLeft className='size-6' aria-hidden />
        </Button>
      )}
      <Heading
        className={cn(
          'text-heading-m-semibold',
          {
            'text-heading-xl-semibold': isDesktop && !isSubpage,
            'text-heading-s-semibold': !isDesktop && isSubpage,
          },
          className,
        )}
      >
        {children}
      </Heading>
    </div>
  );
};
